import { LockedFeature } from '@/app/components'
import SurveyObserver from '@/app/components/SurveyObserver'
import ChargeForecastDashboardView from '@/authenticated/components/ChargeForecastDashboardView'
import ErrorBoundary, {
  LocationTag,
} from '@/authenticated/components/ErrorBoundary'
import VehicleChargerChargesDashboardView from '@/authenticated/components/VehicleChargerChargesDashboardView'
import PlugSessionsPage from '@/app/pages/devices/vehicle/PlugSessionsPage'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { ID } from '@/types/model'
import UtilityProgramLearnMoreDashboardView from 'features/utility-programs/UtilityProgramLearnMoreDashboardView'
import { remoteConfig } from '@/firebase'
import DeviceDREventAlertMessage from '@/app/features/vehicleDemandResponseEvent/DeviceDREventAlertMessage'
import useLegacyAvailablePrograms from '@/app/hooks/useLegacyAvailablePrograms'

export interface Props {
  vehicleChargerId: ID
}

function useViewModel(props: Props) {
  return {
    vehicleChargerId: props.vehicleChargerId,
  }
}

function VehicleChargerSectionRoot(props: ReturnType<typeof useViewModel>) {
  // TODO AVild - isAva nonsense
  const avaId = remoteConfig?.getWhitelabelId('ava')
  const { firstAvailableProgram } = useLegacyAvailablePrograms()
  const isAva = firstAvailableProgram && firstAvailableProgram.id === avaId
  // Same user-id % rollout as the vehicle section (defaults to 0%)
  const showPlugSessions = useFeatureFlag({
    flag: 'plug_sessions',
    experimentalValue: true,
    rollout: true,
  })

  return (
    <>
      <VehicleChargerAlerts />
      <DeviceDREventAlertMessage
        deviceId={props.vehicleChargerId}
        deviceType="vehicleCharger"
      />
      {isAva && <UtilityProgramLearnMoreDashboardView />}
      <ErrorBoundary
        location={LocationTag.VehicleChargerChargeForecastCard}
        functionalityDescription="Vehicle charger charge forecast card"
        fallbackOnCard
      >
        <LockedFeature
          title="Charge Forecast"
          connectionRequirements={['plan', 'unpendingPlan']}
        >
          <ChargeForecastDashboardView
            vehicleChargerId={props.vehicleChargerId}
          />
        </LockedFeature>
      </ErrorBoundary>
      {/* Mirrors VehicleSection: the new Plug Sessions list REPLACES the
          legacy charges list inside the rollout percentage */}
      {showPlugSessions ? (
        <PlugSessionsPage chargerId={Number(props.vehicleChargerId)} />
      ) : (
        <ErrorBoundary
          location={LocationTag.VehicleChargerChargesCard}
          functionalityDescription="Vehicle charger charge forecast card"
          fallbackOnCard
        >
          <VehicleChargerChargesDashboardView
            vehicleChargerId={props.vehicleChargerId}
          />
        </ErrorBoundary>
      )}
    </>
  )
}

export default function VehicleChargerSection(props: Props) {
  const viewModel = useViewModel(props)
  return <VehicleChargerSectionRoot {...viewModel} />
}

function VehicleChargerAlerts() {
  return <SurveyObserver observedSurveys={['ClarkEVSE']} />
}
